import {
  Alert,
  Divider,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { useDomainServices } from "@/providers/DomainServices";
import {
  type AdminAgentThreadsFilters,
  useAdminAgentThreads,
} from "@/shared/domain/admin/hooks/useAdminAgentThreads";
import {
  type AdminFeedbackFilters,
  useAdminFeedback,
  useAdminFeedbackCountsForAgentThreads,
} from "@/shared/domain/admin/hooks/useAdminFeedback";
import { type AgentFeedbackRecord } from "@/shared/domain/agentThread";

import { AdminAgentThreadViewer } from "./AdminAgentThreadViewer";
import {
  AgentThreadFilters,
  type AgentThreadFiltersValues,
} from "./AgentThreadFilters";
import { AgentThreadsTable } from "./AgentThreadsTable";
import styles from "./ExploreAgentThreads.module.css";
import {
  nextExploreUrlParams,
  shouldHydrateFromUrl,
  type ViewMode,
  viewModeFromUrl,
} from "./exploreUrlSync";
import { FeedbackFilters, type FeedbackFiltersValues } from "./FeedbackFilters";
import { FeedbackTable } from "./FeedbackTable";
import { FeedbackTriagePanel } from "./FeedbackTriagePanel";
import { type FilterOption } from "./FilterPanel";
import {
  buildAdminAgentThreadLink,
  buildAdminFeedbackLink,
} from "./shareLinks";
import { usePaginatedFilters } from "./usePaginatedFilters";

const PAGE_SIZE = 20;

const EMPTY_AGENT_THREAD_FILTERS: AgentThreadFiltersValues = {
  org: null,
  user: null,
  sessionId: "",
  customerOnly: false,
  hasErrors: "any",
};

const EMPTY_AGENT_THREAD_ACTIVE: AdminAgentThreadsFilters = {};

const EMPTY_FEEDBACK_FILTERS: FeedbackFiltersValues = {
  sentiment: "",
  category: "",
  resolved: "any",
  hasNotes: "any",
  hasErrors: "any",
  org: null,
  user: null,
  sessionId: "",
  customerOnly: false,
};

const EMPTY_FEEDBACK_ACTIVE: AdminFeedbackFilters = {};

function agentThreadFiltersToActive(
  draft: AgentThreadFiltersValues,
): AdminAgentThreadsFilters {
  return {
    orgId: draft.org?.id,
    createdBy: draft.user?.id,
    sessionId: draft.sessionId || undefined,
    hasErrors:
      draft.hasErrors === "any" ? undefined : draft.hasErrors === "yes",
  };
}

function feedbackFiltersToActive(
  draft: FeedbackFiltersValues,
): AdminFeedbackFilters {
  return {
    sentiment: draft.sentiment || undefined,
    category: draft.category || undefined,
    orgId: draft.org?.id,
    createdBy: draft.user?.id,
    sessionId: draft.sessionId || undefined,
    resolved:
      draft.resolved === "any" ? undefined : draft.resolved === "resolved",
    hasNotes: draft.hasNotes === "any" ? undefined : draft.hasNotes === "yes",
    hasErrors:
      draft.hasErrors === "any" ? undefined : draft.hasErrors === "yes",
  };
}

export function ExploreAgentThreads() {
  // Single hook call — used for both ``orgService`` (filter dropdowns) and
  // ``adminService`` (deep-link hydration). Calling ``useDomainServices``
  // twice in the same component is fine but easy to drift out of sync.
  const { orgService, adminService } = useDomainServices();
  const [searchParams, setSearchParams] = useSearchParams();

  // View mode is driven by React state rather than searchParams because
  // direct ``setViewMode`` calls and the deep-link sync effect used to race
  // over the same URL slot. A single effect below reflects state → URL.
  const [viewMode, setViewMode] = React.useState<ViewMode>(() =>
    viewModeFromUrl(searchParams),
  );

  // Fetch all orgs for autocomplete
  const { data: allOrgs = [], isLoading: orgsLoading } = useQuery({
    queryKey: ["allOrgs"],
    queryFn: ({ signal }) => orgService.getAllOrgs({ abortSignal: signal }),
    staleTime: 60_000,
  });

  // Fetch users for each org
  const orgUserQueries = useQueries({
    queries: allOrgs.map((org) => ({
      queryKey: ["orgUsers", org.org_id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        orgService.getOrgUsers(org.org_id, { abortSignal: signal }),
      staleTime: 60_000,
      enabled: allOrgs.length > 0,
    })),
  });

  const usersLoading = orgUserQueries.some((q) => q.isLoading);

  const orgOptions: FilterOption[] = React.useMemo(
    () =>
      allOrgs.map((o) => ({
        label: `${o.name} (${o.org_id})`,
        id: o.org_id,
      })),
    [allOrgs],
  );

  const userOptions: FilterOption[] = React.useMemo(() => {
    const seen = new Set<string>();
    const options: FilterOption[] = [];
    for (const query of orgUserQueries) {
      for (const orgUser of query.data ?? []) {
        if (!seen.has(orgUser.user.user_id)) {
          seen.add(orgUser.user.user_id);
          const displayName = orgUser.user.name
            ? `${orgUser.user.name} (${orgUser.user.user_id})`
            : orgUser.user.user_id;
          options.push({
            label: displayName,
            id: orgUser.user.user_id,
          });
        }
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [orgUserQueries]);

  const [selectedAgentThreadId, setSelectedAgentThreadId] = React.useState<
    string | undefined
  >();
  const [scrollToMessageSequenceNum, setScrollToMessageSequenceNum] =
    React.useState<number | undefined>();
  const [selectedFeedback, setSelectedFeedback] = React.useState<
    AgentFeedbackRecord | undefined
  >();

  // ---- AgentThreads view state ----
  const agentThreadState = usePaginatedFilters<
    AgentThreadFiltersValues,
    AdminAgentThreadsFilters
  >({
    emptyDraft: EMPTY_AGENT_THREAD_FILTERS,
    emptyActive: EMPTY_AGENT_THREAD_ACTIVE,
    toActive: agentThreadFiltersToActive,
  });

  const agentThreadQueryFilters: AdminAgentThreadsFilters = React.useMemo(
    () => ({
      ...agentThreadState.active,
      pageToken: agentThreadState.pageToken,
      limit: PAGE_SIZE,
    }),
    [agentThreadState.active, agentThreadState.pageToken],
  );

  const { data: agentThreadsData, isLoading: agentThreadsLoading } =
    useAdminAgentThreads(agentThreadQueryFilters);
  const agentThreads = agentThreadsData?.items ?? [];

  // Feedback counts are resolved server-side from the same filter set as the
  // threads listing — no need to ship the page's session ids across the wire.
  const feedbackCounts = useAdminFeedbackCountsForAgentThreads({
    orgId: agentThreadState.active.orgId,
    createdBy: agentThreadState.active.createdBy,
    sessionId: agentThreadState.active.sessionId,
  });

  // ---- Feedback view state ----
  const feedbackState = usePaginatedFilters<
    FeedbackFiltersValues,
    AdminFeedbackFilters
  >({
    emptyDraft: EMPTY_FEEDBACK_FILTERS,
    emptyActive: EMPTY_FEEDBACK_ACTIVE,
    toActive: feedbackFiltersToActive,
  });

  const feedbackQueryFilters: AdminFeedbackFilters = React.useMemo(
    () => ({
      ...feedbackState.active,
      pageToken: feedbackState.pageToken,
      limit: PAGE_SIZE,
    }),
    [feedbackState.active, feedbackState.pageToken],
  );

  const { data: feedbackData, isLoading: feedbackLoading } =
    useAdminFeedback(feedbackQueryFilters);
  const feedbackRows = feedbackData?.items ?? [];

  // ---- Handlers ----
  const handleAgentThreadSearch = () => {
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    agentThreadState.search();
  };

  const handleAgentThreadClear = () => {
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    agentThreadState.clear();
  };

  const handleFeedbackSearch = () => {
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    feedbackState.search();
  };

  const handleFeedbackClear = () => {
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    feedbackState.clear();
  };

  const handleSelectAgentThread = (threadId: string) => {
    setSelectedAgentThreadId(threadId);
    setScrollToMessageSequenceNum(undefined);
    setSelectedFeedback(undefined);
  };

  const handleBack = () => {
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    setSelectedFeedback(undefined);
  };

  const handleSelectFeedback = (feedback: AgentFeedbackRecord) => {
    setSelectedAgentThreadId(feedback.thread_id);
    setScrollToMessageSequenceNum(feedback.message_sequence_num);
    setSelectedFeedback(feedback);
  };

  const handleShowFeedbackForAgentThread = (threadId: string) => {
    // Flip the toggle and pre-filter to this session. ``searchWith`` commits
    // the new draft into ``active`` in one shot — using ``setDraft`` then
    // ``search()`` would commit the closed-over previous draft, since the
    // setDraft hasn't taken effect yet at search-time.
    feedbackState.searchWith({
      ...EMPTY_FEEDBACK_FILTERS,
      sessionId: threadId,
    });
    setSelectedAgentThreadId(undefined);
    setScrollToMessageSequenceNum(undefined);
    setViewMode("feedback");
  };

  // ---- Deep-link hydration / URL sync ----
  // URL is the source of truth for ``sessionId`` and ``feedbackId`` so admins
  // can share links to a specific thread or feedback entry. On mount we copy
  // those params into state; subsequent state changes write back to the URL.
  //
  // The writer is gated on ``hasHydrated`` so it does not fire on the very
  // first render and clobber params we are still waiting on (the feedback
  // hydration path is async — an unguarded writer would delete the
  // ``feedbackId`` we just read from the URL before the fetch resolved).
  const urlSessionId = searchParams.get("sessionId") ?? undefined;
  const urlFeedbackId = searchParams.get("feedbackId") ?? undefined;

  const [hasHydrated, setHasHydrated] = React.useState<boolean>(
    // Nothing to hydrate → we can write the URL freely from first render.
    () => !shouldHydrateFromUrl(searchParams),
  );
  const [hydrationError, setHydrationError] = React.useState<string | null>(
    null,
  );

  // Fetch the feedback record when we land on a shared link, switch to the
  // feedback view (a ``feedbackId``-only deep link implies "show me this
  // feedback" — landing in threads view would show the thread with an unused
  // selection in the parent panel), and flip ``hasHydrated`` once state
  // catches up.
  React.useEffect(() => {
    if (!urlFeedbackId) {
      return;
    }
    if (selectedFeedback?.feedback_id === urlFeedbackId) {
      setHasHydrated(true);
      return;
    }
    let cancelled = false;
    void adminService
      .getAdminFeedbackById(urlFeedbackId)
      .then((record) => {
        if (cancelled) {
          return;
        }
        setSelectedFeedback(record);
        setSelectedAgentThreadId(record.thread_id);
        setScrollToMessageSequenceNum(record.message_sequence_num);
        // Force feedback view so the triage panel actually renders; without
        // this the URL writer would later emit ``view=feedback`` once state
        // settles, but the user briefly sees the threads view first.
        setViewMode("feedback");
        feedbackState.setDraft((prev) => ({
          ...prev,
          sessionId: record.thread_id,
        }));
        feedbackState.setActive((prev) => ({
          ...prev,
          sessionId: record.thread_id,
        }));
        setHasHydrated(true);
      })
      .catch((error: unknown) => {
        // Bad/stale feedbackId shouldn't freeze the UI — allow the writer to
        // proceed so the URL reflects reality, and surface what happened so
        // the admin isn't left staring at an empty triage panel.
        if (cancelled) {
          return;
        }
        setHasHydrated(true);
        const detail = error instanceof Error ? error.message : "unknown error";
        setHydrationError(
          `Couldn't load the feedback from the shared link (${detail}). It may have been deleted.`,
        );
      });
    return () => {
      cancelled = true;
    };
    // feedbackState setters are stable; intentionally excluded to avoid
    // re-running the hydration fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFeedbackId, selectedFeedback?.feedback_id, adminService]);

  // Simpler case: only a sessionId is shared. Select the thread directly.
  // Gated on ``!hasHydrated`` so this only fires once at mount. Without the
  // gate it fought the URL writer below: a user click updated state in the
  // same render whose closure still saw the old ``urlSessionId``, so this
  // effect reverted state while the writer pushed the new URL — flipping
  // back and forth between two session ids indefinitely.
  React.useEffect(() => {
    if (hasHydrated) {
      return;
    }
    if (urlFeedbackId) {
      return; // handled by the feedback-hydration effect
    }
    if (!urlSessionId) {
      return;
    }
    if (selectedAgentThreadId !== urlSessionId) {
      setSelectedAgentThreadId(urlSessionId);
      setSelectedFeedback(undefined);
      setScrollToMessageSequenceNum(undefined);
      if (viewMode === "feedback") {
        feedbackState.setDraft((prev) => ({
          ...prev,
          sessionId: urlSessionId,
        }));
        feedbackState.setActive((prev) => ({
          ...prev,
          sessionId: urlSessionId,
        }));
      }
    }
    setHasHydrated(true);
    // feedbackState setters are stable; intentionally excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasHydrated,
    urlFeedbackId,
    urlSessionId,
    selectedAgentThreadId,
    viewMode,
  ]);

  // Single point-of-truth writer for the URL: ``view``, ``sessionId``,
  // ``feedbackId``. Other params (``tab`` set by Admin.tsx) are preserved.
  // Copying the browser URL at any moment yields a working share link.
  React.useEffect(() => {
    if (!hasHydrated) {
      return;
    }
    const next = nextExploreUrlParams(searchParams, {
      viewMode,
      selectedSessionId: selectedFeedback?.thread_id ?? selectedAgentThreadId,
      selectedFeedbackId: selectedFeedback?.feedback_id,
    });
    if (next !== null) {
      setSearchParams(next, { replace: true });
    }
    // Intentionally exclude searchParams/setSearchParams from deps — they
    // change identity each render and would retrigger the effect in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasHydrated,
    viewMode,
    selectedAgentThreadId,
    selectedFeedback?.feedback_id,
    selectedFeedback?.thread_id,
  ]);

  return (
    <div className={styles.container}>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        size="small"
        onChange={(_e, value: unknown) => {
          if (value === "agentThreads" || value === "feedback") {
            setViewMode(value);
          }
        }}
        aria-label="Explore Threads view mode"
      >
        <ToggleButton value="agentThreads">Threads</ToggleButton>
        <ToggleButton value="feedback">Feedback</ToggleButton>
      </ToggleButtonGroup>

      {viewMode === "agentThreads" ? (
        <>
          <AgentThreadFilters
            values={agentThreadState.draft}
            onChange={agentThreadState.setDraft}
            onSearch={handleAgentThreadSearch}
            onClear={handleAgentThreadClear}
            isLoading={agentThreadsLoading}
            orgOptions={orgOptions}
            userOptions={userOptions}
            orgOptionsLoading={orgsLoading}
            userOptionsLoading={usersLoading}
          />

          <Divider />

          <AgentThreadsTable
            agentThreads={agentThreads}
            isLoading={agentThreadsLoading}
            selectedAgentThreadId={selectedAgentThreadId}
            onSelectAgentThread={handleSelectAgentThread}
            feedbackCountsByAgentThread={feedbackCounts.data}
            onOpenFeedbackForAgentThread={handleShowFeedbackForAgentThread}
            hasNextPage={!!agentThreadsData?.next_token}
            hasPreviousPage={agentThreadState.hasPreviousPage}
            onNextPage={() => {
              if (agentThreadsData?.next_token) {
                agentThreadState.nextPage(agentThreadsData.next_token);
              }
            }}
            onPreviousPage={agentThreadState.previousPage}
            currentPage={agentThreadState.currentPage}
          />
        </>
      ) : (
        <>
          <FeedbackFilters
            values={feedbackState.draft}
            onChange={feedbackState.setDraft}
            onSearch={handleFeedbackSearch}
            onClear={handleFeedbackClear}
            isLoading={feedbackLoading}
            orgOptions={orgOptions}
            userOptions={userOptions}
            orgOptionsLoading={orgsLoading}
            userOptionsLoading={usersLoading}
          />

          <Divider />

          <FeedbackTable
            rows={feedbackRows}
            isLoading={feedbackLoading}
            hasNextPage={!!feedbackData?.next_token}
            hasPreviousPage={feedbackState.hasPreviousPage}
            onNextPage={() => {
              if (feedbackData?.next_token) {
                feedbackState.nextPage(feedbackData.next_token);
              }
            }}
            onPreviousPage={feedbackState.previousPage}
            currentPage={feedbackState.currentPage}
            onSelectFeedback={handleSelectFeedback}
            selectedFeedbackId={selectedFeedback?.feedback_id}
          />
          {selectedFeedback && (
            <FeedbackTriagePanel
              feedback={
                feedbackRows.find(
                  (r) => r.feedback_id === selectedFeedback.feedback_id,
                ) ?? selectedFeedback
              }
            />
          )}
        </>
      )}

      {selectedAgentThreadId && (
        <>
          <Divider />
          <AdminAgentThreadViewer
            threadId={selectedAgentThreadId}
            onBack={handleBack}
            scrollToMessageSequenceNum={scrollToMessageSequenceNum}
            onViewFeedbackForAgentThread={() =>
              handleShowFeedbackForAgentThread(selectedAgentThreadId)
            }
            onAgentThreadSelected={handleSelectAgentThread}
            shareUrl={
              selectedFeedback
                ? buildAdminFeedbackLink({
                    sessionId: selectedFeedback.thread_id,
                    feedbackId: selectedFeedback.feedback_id,
                  })
                : buildAdminAgentThreadLink({
                    sessionId: selectedAgentThreadId,
                  })
            }
          />
        </>
      )}

      <Snackbar
        open={hydrationError !== null}
        autoHideDuration={6000}
        onClose={() => setHydrationError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setHydrationError(null)}
        >
          {hydrationError}
        </Alert>
      </Snackbar>
    </div>
  );
}
